import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import {
  documentDetailQueryOptions,
  isCurrentApproved,
  recordDocumentView,
} from '@/entities/document'
import { useSession } from '@/entities/session'
import { entitySlotsQueryOptions } from '@/entities/slot'
import { EntityTagsEditor } from '@/entities/tag'
import { AddVersionDialog } from '@/features/add-document-version'
import { ApproveButton } from '@/features/approve-document'
import { EditDocumentDialog } from '@/features/edit-document'
import { PublishDocumentButton } from '@/features/publish-document'
import { useSearchStore } from '@/features/search'
import { VisibilityButton } from '@/features/set-document-visibility'
import { ApiError } from '@/shared/api'
import { Breadcrumb, type Crumb } from '@/shared/ui/breadcrumb'
import { Button } from '@/shared/ui/button'
import { Chip } from '@/shared/ui/chip'
import { Icon } from '@/shared/ui/icon'
import { Spinner } from '@/shared/ui/spinner'
import { StatusBadge } from '@/shared/ui/status-badge'
import { StatusScreen } from '@/shared/ui/status-screen'
import { CommentThread } from '@/widgets/comment-thread'
import { EntityPageHeader, EntityPageLayout, type EntityPageTab, Meta } from '@/widgets/entity-page'
import { DocumentFileTab } from './document-file-tab'
import { DocumentViewsTab } from './document-views-tab'

// Лог просмотров — employee-only, поэтому у заказчика набор табов урезан.
export const DOC_TABS = ['file', 'comments', 'views'] as const
export type DocTab = (typeof DOC_TABS)[number]

const TAB_LABELS: Record<DocTab, string> = {
  file: 'Файл',
  comments: 'Комментарии',
  views: 'Просмотры',
}

interface DocumentPageProps {
  documentId: string
  /** Крошки-предки (Компания › … › Документы). Компонент добавляет заголовок документа. */
  breadcrumbAncestors: Crumb[]
  activeTab: DocTab
  onTabChange: (tab: DocTab) => void
  /** Куда уйти после удаления документа (к списку документов владельца). */
  onDeleted: () => void
}

// Full-width страница документа: EntityPageHeader (заголовок/версия/статус/теги/
// действия) + табы file|comments|views. Ноль знания о владельце — крошки и
// навигация приходят от роута; переиспользуется под проект и под компанию.
export function DocumentPageView({
  documentId,
  breadcrumbAncestors,
  activeTab,
  onTabChange,
  onDeleted,
}: DocumentPageProps) {
  const { session } = useSession()
  const isEmployee = session?.kind === 'employee'
  const isCustomer = session?.kind === 'customer'

  const docQ = useQuery(documentDetailQueryOptions(documentId))
  const slotsQ = useQuery({
    ...entitySlotsQueryOptions(docQ.data?.entity_kind ?? 'project', docQ.data?.entity_id ?? ''),
    enabled: docQ.data != null,
  })
  const [addVersionOpen, setAddVersionOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)

  // Авто-фиксация просмотра заказчиком (side-effect, не UI-действие).
  useEffect(() => {
    if (isCustomer) void recordDocumentView(documentId)
  }, [isCustomer, documentId])

  if (docQ.isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    )
  }
  if (docQ.error) {
    const e = docQ.error
    if (e instanceof ApiError && e.isForbidden) {
      return <StatusScreen title="Нет доступа" message="Документ недоступен." />
    }
    if (e instanceof ApiError && e.status === 404) {
      return <StatusScreen title="Документ не найден" message="Проверьте адрес." />
    }
    return <StatusScreen title="Не удалось загрузить документ" message="Обновите страницу." />
  }

  const doc = docQ.data
  const approved = isCurrentApproved(doc)
  const slotName = slotsQ.data?.find((s) => s.id === doc.slot_id)?.name

  const tabValues: readonly DocTab[] = isEmployee ? DOC_TABS : (['file', 'comments'] as const)
  const active: DocTab = tabValues.includes(activeTab) ? activeTab : 'file'

  const tabs: EntityPageTab<DocTab>[] = tabValues.map((value) => ({
    value,
    label: TAB_LABELS[value],
    content:
      value === 'file' ? (
        <DocumentFileTab document={doc} />
      ) : value === 'comments' ? (
        <CommentThread entityKind="document" entityId={doc.id} />
      ) : (
        <DocumentViewsTab documentId={doc.id} />
      ),
  }))

  const statusChips = (
    <div className="flex flex-wrap items-center gap-2">
      {slotName && (
        <Chip tone="neutral" variant="soft">
          {slotName}
        </Chip>
      )}
      {approved ? (
        <StatusBadge status="approved" />
      ) : (
        <Chip tone="warning" variant="soft">
          не согласовано
        </Chip>
      )}
      {isEmployee && doc.draft && <StatusBadge status="draft" />}
      {isEmployee && !doc.draft && doc.visible_to_customer === false && (
        <Chip tone="neutral" variant="soft">
          скрыт от заказчика
        </Chip>
      )}
      {doc.uploaded_by_customer && (
        <Chip tone="info" variant="soft">
          от заказчика
        </Chip>
      )}
    </div>
  )

  const actions = (
    <>
      {!isEmployee && (
        <ApproveButton
          documentId={doc.id}
          currentVersionNumber={doc.current_version.number}
          approvedCurrent={approved}
        />
      )}
      {isEmployee && doc.draft && <PublishDocumentButton documentId={doc.id} />}
      {isEmployee && !doc.uploaded_by_customer && (
        <VisibilityButton documentId={doc.id} visible={doc.visible_to_customer ?? false} />
      )}
      {isEmployee && (
        <>
          <Button variant="secondary" size="sm" onClick={() => setAddVersionOpen(true)}>
            <Icon name="upload" size={16} />
            Новая версия
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Настройки документа"
            onClick={() => setEditOpen(true)}
          >
            <Icon name="pencil" size={16} />
          </Button>
        </>
      )}
    </>
  )

  const header = (
    <EntityPageHeader
      breadcrumb={<Breadcrumb items={[...breadcrumbAncestors, { label: doc.title }]} />}
      title={doc.title}
      subtitle={
        <span className="mono-num text-xs text-text-tertiary">
          v{doc.current_version.number} · текущая
        </span>
      }
      status={<Meta label="Статус">{statusChips}</Meta>}
      actions={actions}
      {...(isEmployee
        ? {
            tags: (
              <EntityTagsEditor
                entityKind="document"
                entityId={doc.id}
                tags={doc.tags ?? []}
                onTagClick={(tag) => useSearchStore.getState().openWithTag(tag)}
              />
            ),
          }
        : {})}
    />
  )

  return (
    <>
      <EntityPageLayout
        header={header}
        tabs={tabs}
        activeTab={active}
        onTabChange={onTabChange}
        tabsLabel="Разделы документа"
      />

      {addVersionOpen && (
        <AddVersionDialog documentId={doc.id} open onClose={() => setAddVersionOpen(false)} />
      )}
      {editOpen && (
        <EditDocumentDialog
          documentId={doc.id}
          entityKind={doc.entity_kind}
          entityId={doc.entity_id}
          currentTitle={doc.title}
          currentSlotId={doc.slot_id}
          open
          onClose={() => setEditOpen(false)}
          onDeleted={onDeleted}
        />
      )}
    </>
  )
}
